import { Fragment } from "react";
import { motion } from "framer-motion";

export default function SynthLayer() {
  return (
    <section
      data-testid="synth-layer-section"
      className="relative py-12 lg:py-16 border-t border-white/[0.06] overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 900px 500px at 50% 0%, rgba(212, 255, 74, 0.07) 0%, transparent 60%)",
        }}
      />

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-[900px] mb-12 lg:mb-14"
        >
          <div className="font-mono-label text-[#D4FF4A]/70 mb-6 flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#D4FF4A] shadow-[0_0_10px_rgba(212,255,74,0.7)]" />
              Customer intelligence · built for action
            </span>
          </div>
          <h2
            data-testid="synth-heading"
            className="font-display text-white text-[44px] lg:text-[76px] font-semibold leading-[1.02] tracking-[-0.03em] mb-6"
          >
            Make every marketing decision with a{" "}
            <span className="text-[#D4FF4A] hero-glow">real customer in mind.</span>
          </h2>
          <p className="text-lg text-[#A1A1AA] leading-[1.55]">
            ICP Intel turns your customer data into a ranked buyer profile, clearer messaging and channel priorities, and a 30 / 60 / 90-day plan your team can actually use.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-14"
        >
          <PipelineVisual />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-6 lg:gap-8 items-stretch">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            data-testid="synth-skills-card"
          >
            <SkillsFileCard />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col gap-6 lg:gap-8"
            data-testid="synth-dashboard-card"
          >
            <DashboardCard />
            <IntegrationsCard />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function PipelineVisual() {
  const nodes = [
    {
      label: "Input",
      title: "Your enriched customer list",
      meta: "One real customer cohort · up to 5,000 buyers",
      color: "rgba(255,255,255,0.08)",
      accent: "#A1A1AA",
    },
    {
      label: "Analysis",
      title: "We find the growth signal",
      meta: "Enrich · segment · rank · diagnose",
      color: "rgba(212,255,74,0.14)",
      accent: "#D4FF4A",
      pulsing: true,
    },
    {
      label: "Output",
      title: "Intelligence + action plan",
      meta: "Dashboard · diagnosis · 30 / 60 / 90 plan",
      color: "rgba(255,255,255,0.08)",
      accent: "#F5F5F7",
    },
  ];
  return (
    <div className="relative bg-[#05060A] border border-white/[0.08] rounded-2xl p-6 lg:p-8 overflow-hidden">
      <div className="font-mono-label text-[#52525B] text-[9px] mb-6">
        From customer data to your next 90 days
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-5">
        {nodes.map((n, i) => (
          <Fragment key={n.label}>
            <div
              className="relative rounded-xl border px-5 py-5 overflow-hidden"
              style={{ borderColor: `${n.accent}35`, background: n.color }}
            >
              {n.pulsing && (
                <div
                  aria-hidden
                  className="absolute inset-0 opacity-60 animate-pulse pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(circle at 50% 50%, rgba(212,255,74,0.14), transparent 70%)",
                  }}
                />
              )}
              <div className="relative">
                <div
                  className="font-mono-label text-[10px] mb-2"
                  style={{ color: n.accent }}
                >
                  {n.label}
                </div>
                <div className="font-display text-white text-[18px] lg:text-[20px] font-semibold leading-tight mb-2">
                  {n.title}
                </div>
                <div className="font-mono text-[#71717A] text-[10px] leading-[1.5]">
                  {n.meta}
                </div>
              </div>
            </div>
            {i < nodes.length - 1 && (
              <div className="hidden md:flex items-center justify-center text-[#D4FF4A]/70 text-[20px] px-1">
                →
              </div>
            )}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function SkillsFileCard() {
  const files = [
    { name: "00-readme.md", active: false },
    { name: "01-core-buyer-voice.md", active: true },
    { name: "02-agency-owner-playbook.md", active: false },
    { name: "03-saas-founder-playbook.md", active: false },
    { name: "04-creator-playbook.md", active: false },
    { name: "05-channel-map.md", active: false },
    { name: "06-copy-intelligence.md", active: false },
  ];

  return (
    <div className="relative h-full bg-[#05060A] border border-white/[0.08] rounded-2xl overflow-hidden shadow-[0_50px_100px_-50px_rgba(0,0,0,0.9)]">
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.05] bg-white/[0.015]">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-white/10" />
          <div className="w-2.5 h-2.5 rounded-full bg-white/10" />
          <div className="w-2.5 h-2.5 rounded-full bg-white/10" />
        </div>
        <div className="font-mono text-[#52525B] text-[11px] tracking-[0.06em]">
          SKILLS / 01-core-buyer-voice.md
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#D4FF4A]/10 border border-[#D4FF4A]/25 text-[#D4FF4A] text-[9px] font-mono">
            <span className="w-1 h-1 rounded-full bg-[#D4FF4A] animate-pulse" />
            AUTO-SYNC
          </span>
        </div>
      </div>

      <div className="grid grid-cols-[180px_1fr] min-h-[560px]">
        <div className="border-r border-white/[0.05] bg-white/[0.01] py-4">
          <div className="px-4 mb-3 flex items-center gap-2">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
              <path
                d="M2 3h5l1 2h6v8H2V3z"
                stroke="#71717A"
                strokeWidth="1.2"
                fill="rgba(113,113,122,0.15)"
              />
            </svg>
            <span className="font-mono text-[#A1A1AA] text-[11px]">SKILLS</span>
          </div>
          <div className="space-y-0.5">
            {files.map((f) => (
              <div
                key={f.name}
                className={`px-4 py-1.5 flex items-center gap-2 ${
                  f.active
                    ? "bg-[#D4FF4A]/10 border-l-2 border-[#D4FF4A]"
                    : "hover:bg-white/[0.03]"
                }`}
              >
                <span
                  className="font-mono text-[11px] truncate"
                  style={{ color: f.active ? "#D4FF4A" : "#71717A" }}
                >
                  {f.name}
                </span>
              </div>
            ))}
          </div>
          <div className="px-4 mt-5 pt-4 border-t border-white/[0.04]">
            <div className="font-mono text-[#52525B] text-[9px] tracking-[0.14em] uppercase mb-2">
              Last sync
            </div>
            <div className="font-mono text-[#A1A1AA] text-[10px]">
              2026-04-12 · 06:14 UTC
            </div>
          </div>
        </div>

        <div className="p-6 lg:p-7 overflow-hidden">
          <pre className="font-mono text-[12.5px] leading-[1.75] text-[#D4D4D8] whitespace-pre-wrap">
            <span className="text-[#52525B]">{"---"}</span>
            {"\n"}
            <span className="text-[#D4FF4A]">name</span>
            <span className="text-[#52525B]">:</span>{" "}
            <span className="text-white">core-buyer-voice</span>
            {"\n"}
            <span className="text-[#D4FF4A]">description</span>
            <span className="text-[#52525B]">:</span>{" "}
            <span className="text-white">
              Write copy to Marcus Delgado, not marketing managers
            </span>
            {"\n"}
            <span className="text-[#D4FF4A]">segment</span>
            <span className="text-[#52525B]">:</span>{" "}
            <span className="text-white">agency-owner</span>
            {"\n"}
            <span className="text-[#D4FF4A]">priority</span>
            <span className="text-[#52525B]">:</span>{" "}
            <span className="text-white">core</span>
            {"\n"}
            <span className="text-[#D4FF4A]">last_synthesized</span>
            <span className="text-[#52525B]">:</span>{" "}
            <span className="text-white">2026-04-12</span>
            {"\n"}
            <span className="text-[#52525B]">{"---"}</span>
            {"\n\n"}
            <span className="font-display text-white text-[17px] font-semibold">
              ## When to use this skill
            </span>
            {"\n\n"}
            Any outbound marketing, ad copy, or landing page for the Agency
            Owner segment. Apply whenever audience equals small agency owner
            with a 3 to 15 person client services team.
            {"\n\n"}
            <span className="font-display text-white text-[17px] font-semibold">
              ## Voice
            </span>
            {"\n\n"}
            Direct. Numbers-first. Talks about margin, retainer, ops-light.
            Hates{" "}
            <span className="text-[#EF4444]/80 line-through decoration-1">
              synergies
            </span>
            ,{" "}
            <span className="text-[#EF4444]/80 line-through decoration-1">
              enterprise-grade
            </span>
            ,{" "}
            <span className="text-[#EF4444]/80 line-through decoration-1">
              AI-first
            </span>
            .{"\n"}
            Uses <span className="text-[#D4FF4A]">ops-light</span>,{" "}
            <span className="text-[#D4FF4A]">scope creep</span>,{" "}
            <span className="text-[#D4FF4A]">white label</span>.
            {"\n\n"}
            <span className="font-display text-white text-[17px] font-semibold">
              ## Example campaign angle
            </span>
            {"\n\n"}
            <span className="text-[#A1A1AA]">{">"}</span> "Marcus, this shaves 4
            hours off every client onboarding"{"\n"}
            <span className="text-[#A1A1AA]">{">"}</span>{" "}
            <span className="text-[#D4FF4A]">Built from the segment's buying triggers and language.</span>
          </pre>
        </div>
      </div>
    </div>
  );
}

function DashboardCard() {
  return (
    <div className="relative flex-1 bg-[#05060A] border border-white/[0.08] rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.05] bg-white/[0.015]">
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-white/10" />
          <div className="w-2 h-2 rounded-full bg-white/10" />
          <div className="w-2 h-2 rounded-full bg-white/10" />
        </div>
        <div className="font-mono text-[#52525B] text-[10px] tracking-[0.06em]">
          report.icpintel.ai / your-brand
        </div>
        <div className="w-10" />
      </div>
      <div className="p-6">
        <div className="font-mono-label text-[#52525B] text-[9px] mb-2">
          Live dashboard
        </div>
        <div className="font-display text-white text-[22px] font-semibold leading-tight mb-4">
          Your customer intelligence, ready to use.
        </div>
        <div className="grid grid-cols-2 gap-3 mb-5">
          <MiniStat v="5K" l="Buyers analyzed" />
          <MiniStat v="4" l="Segments" accent />
          <MiniStat v="1" l="Core avatar" />
          <MiniStat v="90d" l="Action plan" />
        </div>
        <div className="space-y-2 mb-4">
          <BuyerPill name="Marcus Delgado" ltv="$26,412" />
          <BuyerPill name="Dominic Reyes" ltv="$25,891" />
          <BuyerPill name="Elena Moretti" ltv="$22,104" />
        </div>
        <div className="text-[#71717A] text-[11px]">
          Drill into the buyers, segments, language, and channels behind the recommendation.
        </div>
      </div>
    </div>
  );
}

function MiniStat({ v, l, accent = false }) {
  return (
    <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.05]">
      <div
        className={`font-display text-[22px] font-semibold leading-none mb-1 tracking-tight ${
          accent ? "text-[#D4FF4A]" : "text-white"
        }`}
      >
        {v}
      </div>
      <div className="font-mono text-[#71717A] text-[9px] tracking-[0.12em] uppercase">
        {l}
      </div>
    </div>
  );
}

function BuyerPill({ name, ltv }) {
  return (
    <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-[#0A0B10] border border-white/[0.05]">
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="w-5 h-5 rounded-full bg-[#D4FF4A]/10 border border-[#D4FF4A]/25 shrink-0" />
        <span className="text-white text-[12px] font-medium truncate">
          {name}
        </span>
      </div>
      <span className="font-mono text-[#D4FF4A] text-[11px] shrink-0">{ltv}</span>
    </div>
  );
}

function IntegrationsCard() {
  const tools = [
    "Claude",
    "ChatGPT",
    "Cursor",
    "Notion",
    "Slack",
    "Figma",
    "Zed",
    "Linear",
  ];
  return (
    <div className="relative bg-[#05060A] border border-white/[0.08] rounded-2xl p-6">
      <div className="font-mono-label text-[#52525B] text-[9px] mb-3">
        Drops into
      </div>
      <div className="font-display text-white text-[18px] font-semibold leading-tight mb-4">
        Wherever your team already works.
      </div>
      <div className="flex flex-wrap gap-1.5">
        {tools.map((t) => (
          <span
            key={t}
            className="inline-flex items-center px-2.5 py-1 rounded-full bg-white/[0.03] border border-white/[0.08] text-[#D4D4D8] text-[11px]"
          >
            {t}
          </span>
        ))}
      </div>
      <div className="mt-4 pt-4 border-t border-white/[0.05] text-[#71717A] text-[11px] leading-[1.55]">
        One skill file per segment. Imported, referenced, or pasted. Your
        copywriter, your PPC lead, your founder. Same source of truth.
      </div>
    </div>
  );
}
